import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DepotFacade } from '@zero/depot/domain';
import { IconComponent, PillComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { AuthService, DataService, RouteOptionsService, WindowService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { AssetSearchComponent } from '../asset-search/asset-search.component';

@Component({
  selector: 'web-components-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    TranslocoPipe,
    AssetSearchComponent,
    RouterLink,
    RouterLinkActive,
    AsyncPipe,
    NgClass,
    NgTemplateOutlet,
    PillComponent,
    IconComponent,
  ],
})
export class HeaderComponent implements OnInit {
  public isMobileNavigationVisible = false;
  public isPrivateLinksVisible = false;
  public isDepotDropdownVisible = false;
  public isMobileSearchVisible = false;
  public isOrderLinksVisible = false;
  public isMoreDropdownVisible = false;
  public isInvestInDropdownVisible = false;

  public readonly commonRoutes = commonRoutes;

  readonly #dataService = inject(DataService);
  readonly #authService = inject(AuthService);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #router = inject(Router);
  readonly #windowService = inject(WindowService);
  readonly #routeOptionsService = inject(RouteOptionsService);
  readonly #depotFacade = inject(DepotFacade);

  ngOnInit(): void {
    this.#depotFacade.loadUserInterests();
  }

  public readonly isVisible$ = this.#routeOptionsService.routeOptions$.pipe(
    map(({ shouldHideHeaderAndFooter }) => !shouldHideHeaderAndFooter),
  );
  public readonly isLoggedIn$ = this.#authService.isLoggedIn$();
  public readonly isLoggedOut$ = this.isLoggedIn$.pipe(map((isLoggedIn) => !isLoggedIn));
  public readonly isCustomer$ = this.#authService.isCustomer$();
  public readonly isPotentialCustomer$ = this.#authService.isPotentialCustomer();

  public readonly lombardEnabled$ = this.#customerStateFacade.customerBaseData$.pipe(
    map((customerBaseData) => customerBaseData?.lombardEnabled),
  );
  public readonly isAdult$ = this.#customerStateFacade.customerBaseData$.pipe(
    map((customerBaseData) => customerBaseData && !customerBaseData.child),
  );

  public readonly unreadDocs$ = this.#dataService.unreadDocumentsCountChanged$.pipe(
    map(() => this.#dataService.unreadDocuments?.eingang + this.#dataService.unreadDocuments?.mifid),
  );

  public readonly unreadTickets$ = this.#dataService.unreadTicketsCountChanged$.pipe(map(() => this.#dataService.unreadTickets));

  public readonly unreadTotal$ = combineLatest({
    unreadDocs: this.unreadDocs$,
    unreadTickets: this.unreadTickets$,
  }).pipe(
    map(({ unreadDocs, unreadTickets }) => unreadDocs + unreadTickets),
    distinctUntilChanged(),
  );

  public logOut = (): void => this.#authService.explicitLogout();

  public openMobileNavigation = (): void => {
    this.isMobileNavigationVisible = true;
  };

  public closeMobileNavigation = (): void => {
    this.isMobileNavigationVisible = false;
    this.isMobileSearchVisible = false;
  };

  public toggleMobileNavigation = (): void => {
    this.isMobileNavigationVisible = !this.isMobileNavigationVisible;
  };

  public toggleMobileSearch = (): void => {
    this.#windowService.getWindow().scroll(0, 0);
    this.isMobileSearchVisible = !this.isMobileSearchVisible;
  };

  public openPrivateLinks(): void {
    this.isPrivateLinksVisible = true;
    this.isOrderLinksVisible = false;
  }

  public closeDropdowns = (): void => {
    this.closePrivateLinks();
    this.closeDepotDropdownLinks();
    this.closeOrderDropdownLinks();
    this.closeMoreDropdownLinks();
    this.closeInvestInDropdownLinks();
  };

  public closePrivateLinks = (): void => {
    this.isPrivateLinksVisible = false;
  };

  public openDepotDropdownLinks = (): void => {
    this.isDepotDropdownVisible = true;
    this.isPrivateLinksVisible = false;
    this.isOrderLinksVisible = false;
    this.isMoreDropdownVisible = false;
    this.isInvestInDropdownVisible = false;
  };

  public openOrderDropdownLinks = (): void => {
    this.isDepotDropdownVisible = false;
    this.isPrivateLinksVisible = false;
    this.isOrderLinksVisible = true;
    this.isMoreDropdownVisible = false;
    this.isInvestInDropdownVisible = false;
  };

  public openMoreDropdownLinks = (): void => {
    this.isPrivateLinksVisible = false;
    this.isOrderLinksVisible = false;
    this.isDepotDropdownVisible = false;
    this.isMoreDropdownVisible = true;
    this.isInvestInDropdownVisible = false;
  };

  public openInvestInDropdownLinks = (): void => {
    this.isPrivateLinksVisible = false;
    this.isOrderLinksVisible = false;
    this.isDepotDropdownVisible = false;
    this.isMoreDropdownVisible = false;
    this.isInvestInDropdownVisible = true;
  };

  public closeDepotDropdownLinks = (): void => {
    this.isDepotDropdownVisible = false;
  };

  public closeOrderDropdownLinks = (): void => {
    this.isOrderLinksVisible = false;
  };

  public closeMoreDropdownLinks = (): void => {
    this.isMoreDropdownVisible = false;
  };

  public closeInvestInDropdownLinks = (): void => {
    this.isInvestInDropdownVisible = false;
  };

  public openExternal = (url: string): void => {
    this.closeMobileNavigation();
    window.open(url, '_blank');
  };

  public navigateToBuy = (): void => {
    this.#router.navigate([commonRoutes.postlogin.buyAndSell]);
  };
}
